import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
// ThemeToggle removed from sidebar — available in top Header (Phase 9)
import { taskService, propertyService, agreementService, insuranceService, licenseService, workflowEngineService, routingService } from '../services/api';
import { useEntitlements } from '../services/entitlements';
import { usePermissions } from '../services/permissions';
import { AnimatedLogo } from './AnimatedLogo';

interface SidebarProps {
  userRole?: string;
  organizationType?: string; // kept for compat but now derived from role
}

// Shared vector (SVG) icon set for nav items — replaces the emoji glyphs so the
// whole menu uses consistent line icons.
const svgProps = {
  width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
const NAV_ICONS: Record<string, React.ReactNode> = {
  trash: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" /></svg>),
  documents: (<svg {...svgProps}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>),
  slaDashboard: (<svg {...svgProps}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>),
  users: (<svg {...svgProps}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>),
  roles: (<svg {...svgProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>),
  organizations: (<svg {...svgProps}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>),
  departments: (<svg {...svgProps}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>),
  whatsappVendors: (<svg {...svgProps}><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>),
  locations: (<svg {...svgProps}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>),
  approvalMatrix: (<svg {...svgProps}><polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" /></svg>),
  slaConfig: (<svg {...svgProps}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>),
  configurations: (<svg {...svgProps}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>),
  storage: (<svg {...svgProps}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>),
  subscription: (<svg {...svgProps}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>),
  zoho: (<svg {...svgProps}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" /></svg>),
  fieldDefs: (<svg {...svgProps}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>),
  docTypes: (<svg {...svgProps}><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="8" y1="16" x2="13" y2="16" /></svg>),
  // Forensic Detection Layer nav icon — shield with checkmark inside.
  forensic: (<svg {...svgProps}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>),
  settings: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>),
};

const Sidebar: React.FC<SidebarProps> = ({ userRole }) => {
  const location = useLocation();
  const [taskCount, setTaskCount] = useState<number>(0);
  const [approvalCount, setApprovalCount] = useState<number>(0);
  // The current user's effective permissions. Fail CLOSED: a permission-gated
  // link stays hidden until we've confirmed the user actually has the permission
  // (and if the fetch fails). Previously this failed OPEN (null → true), so a
  // failed/slow load briefly showed EVERY gated section (e.g. Compliance to a role
  // without compliance.view). A short hide-then-show on load is the safe trade.
  // Use the SHARED permission cache (services/permissions) — the SAME source the
  // route guards (AdminLayoutRoute) read. Previously the sidebar fetched its own
  // copy via authService.getMyPermissions(), so a nav item and its page could
  // DISAGREE ("Users tab shows but the page says Access Restricted"). One source
  // now guarantees nav visibility and route access always match. Fail closed.
  const { can } = usePermissions();
  const hasPerm = (p: string) => can(p);
  // Phase 15 item 1c — collapsible Settings group (persists across navigations)
  const SETTINGS_OPEN_KEY = 'pb_sidebar_settings_open';
  // Default OPEN on a fresh login (no stored preference) — the Settings group
  // starts expanded so its items are visible without a click. An explicit
  // collapse by the user is still remembered (localStorage 'false').
  const [settingsOpen, setSettingsOpen] = useState<boolean>(() => {
    const v = localStorage.getItem(SETTINGS_OPEN_KEY);
    return v === null ? true : v === 'true';
  });
  useEffect(() => { localStorage.setItem(SETTINGS_OPEN_KEY, String(settingsOpen)); }, [settingsOpen]);


  // Records and Organization are dropdown groups in the menu (not tabbed pages).
  const RECORDS_OPEN_KEY = 'pb_sidebar_records_open';
  // Open by default (no stored choice) — Login clears both keys, so every
  // sign-in starts with Records and Settings expanded.
  const [recordsOpen, setRecordsOpen] = useState<boolean>(() => {
    const v = localStorage.getItem(RECORDS_OPEN_KEY);
    return v === null ? true : v === 'true';
  });
  useEffect(() => { localStorage.setItem(RECORDS_OPEN_KEY, String(recordsOpen)); }, [recordsOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  useEffect(() => {
    taskService.getTaskCounts()
      .then(res => {
        setTaskCount(res.data?.assigned_to_me || 0);
      })
      .catch(() => {});
  }, []);

  // My Workflow Approvals badge — open workflow-engine tasks assigned to this user.
  // Polls so the badge reflects newly-assigned approvals without a page reload; also
  // refreshes when the tab regains focus (cheap query, empty for non-workflow tenants).
  const [wfApprovalCount, setWfApprovalCount] = useState<number>(0);
  useEffect(() => {
    let alive = true;
    // Workflow approvals + finding reviews: both wait for this user in Approvals.
    const refresh = () => {
      // Skip the poll while the tab is in the background (mirrors NotificationBell);
      // a hidden tab shouldn't keep hitting two endpoints every minute.
      if (document.hidden) return Promise.resolve();
      return Promise.all([
        workflowEngineService.myTasks().then(res => res.data?.tasks?.length || 0).catch(() => 0),
        routingService.myReviews().then(res => res.data?.reviews?.length || 0).catch(() => 0),
      ]).then(([a, b]) => { if (alive) setWfApprovalCount(a + b); });
    };
    refresh();
    const id = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    // Fired right after an approve / reject / acknowledge, so the badge follows at once.
    window.addEventListener('approvals:changed', onFocus);
    return () => {
      alive = false; clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('approvals:changed', onFocus);
    };
  }, []);

  // Arbitrator badge — documents waiting to be placed into a department. Held
  // documents are stopped: nobody can approve them until someone acts here, so
  // the count is not informational, it is a queue of blocked work.
  //
  // Deliberately NOT folded into My Approvals. That badge means "a document is
  // waiting for your approve/reject decision"; this means "a fact is missing".
  // Mixing them would make the approvals number mean two things, and a number
  // that means two things stops being read.
  // isAdmin is computed inside the Settings-group closure further down, so the
  // same test is spelled out here rather than hoisting a variable the rest of
  // the file does not expect.
  const canArbitrate = userRole === 'tenant_admin' || userRole === 'super_admin'
    || hasPerm('routing.arbitrate');
  const [arbitratorCount, setArbitratorCount] = useState<number>(0);
  useEffect(() => {
    if (!canArbitrate) return;
    let alive = true;
    const refresh = () => {
      if (document.hidden) return;  // don't poll a backgrounded tab
      routingService.queueCount()
        .then(res => { if (alive) setArbitratorCount(res.data?.count || 0); })
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    // Fired right after an approve / reject / acknowledge, so the badge follows at once.
    window.addEventListener('approvals:changed', onFocus);
    return () => {
      alive = false; clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('approvals:changed', onFocus);
    };
  }, [canArbitrate]);

  // Plan-module gating — hide nav for modules the tenant's plan excludes.
  // Fails open until entitlements load, and super_admin sees everything.
  const { hasModule } = useEntitlements();
  // Documents is gated by the plan AND the user's view_document permission, so a
  // module-only role (e.g. a Licence Administrator) doesn't see the doc queue.
  const showDocs = hasModule('mod_document_processing') && hasPerm('view_document');
  // Insights dashboard — document-intelligence, gated by the org-stats permission.
  // Master Data is a COMMON reference library for every user of the tenant's
  // orgs — gated only by the plan module, not view_document. The backend list
  // is org-scoped, so users still only see their own organizations' documents.
  const showMasterData = hasModule('mod_document_processing');
  const showTasks = hasModule('mod_tasks_compliance');
  // Tasks is org-wide (no permission). Compliance, however, is gated by the user's
  // own compliance.view permission — and the nav MUST match what's actually
  // accessible: the compliance data endpoint returns "Missing permission:
  // compliance.view", so showing the nav to a user without it just leads to a
  // blocked page. Show Compliance only when the tenant owns the module AND the
  // user holds compliance.view (fail closed until perms load).
  // Timesheets are for everyone, in every tenant: no plan module (they no longer
  // ride on Tasks & Compliance) and no permission of their own — your own working
  // day is yours to record. Gating it on timesheet.view hid the module entirely
  // from tenants whose people sit on custom roles, since the migration granting
  // that permission only knows the built-in role names.
  const showTimesheet = true;
  // Records sub-items are gated by BOTH the tenant's plan AND the user's own
  // module view permission, so e.g. a Real Estate Officer only sees Real Estate.
  // Plan-level availability (tenant subscribed to the module).
  const planRealEstate = hasModule('mod_real_estate');
  const planInsurance = hasModule('mod_insurance_licenses');
  // Nav links are gated by BOTH the plan AND the user's own module view
  // permission, so e.g. a Real Estate Officer only sees Real Estate.
  const showRealEstate = planRealEstate && hasPerm('realestate.view');
  const showAgreement = planRealEstate && hasPerm('agreement.view');
  const showInsurance = planInsurance && hasPerm('insurance.view');
  const showLicense = planInsurance && hasPerm('license.view');
  const showRecords = showRealEstate || showAgreement || showInsurance || showLicense;

  // Pending-approval badge — total acknowledgements awaiting the current user
  // across the record modules they can access (mirrors the Tasks badge).
  useEffect(() => {
    // Acks represent records ASSIGNED to this user — reachable even without the
    // module's view permission — so this count is gated by the plan only, not by
    // the nav view-permission. (The endpoints are auth-only + user-scoped.)
    if (!planRealEstate && !planInsurance) return;
    let alive = true;
    const refresh = () => {
      const calls: Promise<any>[] = [];
      if (planRealEstate) {
        calls.push(propertyService.pendingAcknowledgements().catch(() => ({ data: [] })));
        calls.push(agreementService.pendingAcknowledgements().catch(() => ({ data: [] })));
      }
      if (planInsurance) {
        calls.push(insuranceService.pendingAcknowledgements().catch(() => ({ data: [] })));
        calls.push(licenseService.pendingAcknowledgements().catch(() => ({ data: [] })));
      }
      if (calls.length === 0) return;
      Promise.all(calls)
        .then((results) => { if (alive) setApprovalCount(results.reduce((sum, r) => sum + ((r?.data?.length) || 0), 0)); })
        .catch(() => {});
    };
    refresh();
    // Refetch after an acknowledgement is acted on (see InsurancePendingApprovalsPage).
    window.addEventListener('approvals:changed', refresh);
    return () => { alive = false; window.removeEventListener('approvals:changed', refresh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planRealEstate, planInsurance]);

  // Settings is admin-only. Non-admin workflow roles (Accountant/auditor_maker,
  // Reviewer/auditor_checker, maker) must not see any configuration surface.
  // A custom role that holds an admin permission (manage_users/organizations/
  // departments) is a valid admin too, so the group opens for them and each item
  // below gates on its own permission — the generic model, not the role name.
  const isSettingsAdmin = userRole === 'tenant_admin' || userRole === 'super_admin'
    || hasPerm('manage_users') || hasPerm('manage_organizations') || hasPerm('manage_departments')
    || hasPerm('delete_document');

  // The route-layout wrappers (AdminLayoutRoute / ModuleLayoutRoute / …) each
  // render their own <Sidebar>, so navigating between two routes on different
  // wrappers remounts this component and resets the scrollable nav to the top —
  // the "sidebar jumps up" bug. We persist the nav scroll position across those
  // remounts (sessionStorage survives the unmount; localStorage would leak the
  // scroll into a brand-new session). Restore on mount, save on scroll.
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem('pb_sidebar_scroll');
    if (saved) el.scrollTop = Number(saved) || 0;
    const onScroll = () => sessionStorage.setItem('pb_sidebar_scroll', String(el.scrollTop));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="sidebar">
      {/* Phase 9 — centered, smaller PaperBridge logo. Theme toggle removed (lives in Header). */}
      <div className="sidebar-header" style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '20px',
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AnimatedLogo size={26} />
          </div>
          <span
            className="brand-wordmark"
            style={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.01em', fontSize: '16px' }}
          >
            <span style={{ color: '#FFFFFF' }}>Paper</span>
            <span style={{ color: '#4ADE80' }}>Bridge</span>
          </span>
        </div>
      </div>

      <nav className="sidebar-nav" ref={navRef}>
        <div className="nav-section">
          <Link
            to="/dashboard"
            className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </span>
            <span className="nav-label">Dashboard</span>
          </Link>
          {showDocs && (
          <Link
            to="/documents"
            className={`nav-item ${isActive('/documents') || isActive('/auditor') ? 'active' : ''}`}
          >
            <span className="nav-icon">{NAV_ICONS.documents}</span>
            <span className="nav-label">Documents</span>
          </Link>
          )}
          {showTasks && (
          <Link
            to="/tasks"
            className={`nav-item ${isActive('/tasks') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <path d="M9 14l2 2 4-4" />
              </svg>
            </span>
            <span className="nav-label">Tasks</span>
            {taskCount > 0 && (
              <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', minWidth: '18px', textAlign: 'center', lineHeight: '16px' }}>
                {taskCount > 99 ? '99+' : taskCount}
              </span>
            )}
          </Link>
          )}
          {showTasks && hasPerm('compliance.view') && (
          <Link
            to="/compliance"
            className={`nav-item ${isActive('/compliance') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <span className="nav-label">Compliance</span>
          </Link>
          )}
          {(() => {
            // One page, three tabs (Approvals / Arbitrator / Acknowledgements).
            // The badge is the total of what this user can see, split in the tooltip.
            const parts = [
              { label: 'Approvals', count: wfApprovalCount, show: true },
              { label: 'Arbitrator', count: arbitratorCount, show: canArbitrate },
              { label: 'Acknowledgements', count: approvalCount, show: showRecords },
            ].filter(p => p.show);
            const total = parts.reduce((n, p) => n + (p.count || 0), 0);
            return (
              <Link
                to="/approvals"
                className={`nav-item ${location.pathname === '/approvals' ? 'active' : ''}`}
              >
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                    <path d="M22 4L12 14.01l-3-3" />
                  </svg>
                </span>
                <span className="nav-label">Approvals</span>
                {total > 0 && (
                  <span title={parts.filter(p => p.count).map(p => `${p.label}: ${p.count}`).join(' · ')}
                        style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', minWidth: '18px', textAlign: 'center', lineHeight: '16px' }}>
                    {total > 99 ? '99+' : total}
                  </span>
                )}
              </Link>
            );
          })()}
          {showTimesheet && (
          <Link
            to="/timesheets"
            className={`nav-item ${isActive('/timesheets') || isActive('/timesheets/team') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15.5 14" />
              </svg>
            </span>
            <span className="nav-label">Timesheet</span>
          </Link>
          )}
          {showMasterData && (
          <Link
            to="/master-data"
            className={`nav-item ${isActive('/master-data') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
            <span className="nav-label">Master Data</span>
          </Link>
          )}
          {(userRole === 'tenant_admin' || userRole === 'super_admin') && (
          <Link
            to="/catalog"
            className={`nav-item ${isActive('/catalog') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7h16M4 12h16M4 17h10" />
              </svg>
            </span>
            <span className="nav-label">Catalog</span>
          </Link>
          )}
          {(userRole === 'tenant_admin' || userRole === 'super_admin') && (
          <Link
            to="/engagements"
            className={`nav-item ${isActive('/engagements') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
              </svg>
            </span>
            <span className="nav-label">Engagements</span>
          </Link>
          )}
          {(userRole === 'tenant_admin' || userRole === 'super_admin') && (
          <Link
            to="/dossiers"
            className={`nav-item ${isActive('/dossiers') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" />
              </svg>
            </span>
            <span className="nav-label">Dossiers</span>
          </Link>
          )}
          {(userRole === 'tenant_admin' || userRole === 'super_admin') && (
          <Link
            to="/submissions"
            className={`nav-item ${isActive('/submissions') ? 'active' : ''}`}
          >
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </span>
            <span className="nav-label">Submissions</span>
          </Link>
          )}
        </div>

        {/* Records: a dropdown with its four pages, each with its own plan + permission rule. */}
        {showRecords && (() => {
          const items = [
            { to: '/real-estate', label: 'Real Estate', show: showRealEstate },
            { to: '/agreements', label: 'Agreements', show: showAgreement },
            { to: '/insurances', label: 'Insurance', show: showInsurance },
            { to: '/licenses', label: 'Licenses & Renewals', show: showLicense },
          ].filter(i => i.show);
          const here = items.some(i => location.pathname.startsWith(i.to));
          const open = recordsOpen || here;
          return (
            <div className="nav-section">
              <button type="button" onClick={() => setRecordsOpen(v => !v)} className={`nav-item ${here && !open ? 'active' : ''}`}
                style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", textAlign: 'left' }}
                aria-expanded={open}>
                <span className="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </span>
                <span className="nav-label">Records</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     style={{ marginLeft: 'auto', transition: 'transform 0.2s ease', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              {open && items.map(item => (
                <Link key={item.to} to={item.to}
                  className={`nav-item ${location.pathname.startsWith(item.to) ? 'active' : ''}`}
                  style={{ paddingLeft: '46px', fontSize: '12.5px' }}>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>
          );
        })()}

        {/* Phase 15 item 1c — collapsible Settings group. Admin-only: Accountant
            (auditor_maker) / Reviewer (auditor_checker) / maker never see it. */}
        {isSettingsAdmin && (() => {
          const isAdmin = userRole === 'tenant_admin' || userRole === 'super_admin';
          const isSuper = userRole === 'super_admin';
          // Roles, Field Definitions and Document Types are platform-wide
          // facilities defined at the Control Panel — super_admin only. They are
          // intentionally not shown to tenant admins.
          // Grouped pages: each opens with tabs for the screens it holds, and each
          // tab keeps its own access rule (see pages/Hubs.tsx).
          const adminItems = [
            { to: '/organizations-admin',  label: 'Organizations',       icon: 'organizations',  show: isAdmin || hasPerm('manage_organizations') },
            { to: '/departments-admin',    label: 'Departments',         icon: 'departments',    show: isAdmin || hasPerm('manage_departments') },
            { to: '/users',                label: 'Users',               icon: 'users',          show: isAdmin || hasPerm('manage_users') },
            { to: '/property-locations',   label: 'Locations',           icon: 'locations',      show: isAdmin && planRealEstate },
            { to: '/configurations',       label: 'Configurations',      icon: 'configurations', show: isAdmin },
            { to: '/record-types',         label: 'Record Types',        icon: 'slaConfig',      show: isAdmin },
            { to: '/work-types',           label: 'Work Types',          icon: 'slaConfig',      show: isAdmin },
            { to: '/automation',           label: 'Automation',          icon: 'approvalMatrix', show: isAdmin || hasPerm('rules.manage') },
            { to: '/external-master',      label: 'External Master',     icon: 'whatsappVendors', show: isAdmin || hasPerm('routing.arbitrate') },
            { to: '/subscription',         label: 'Subscription & Plan', icon: 'subscription',   show: isAdmin },
            { to: '/platform',             label: 'Platform',            icon: 'roles',          show: isSuper },
          ];
          const visibleItems = adminItems.filter(i => i.show);
          if (visibleItems.length === 0) return null;

          return (
            <div className="nav-section">
              <button
                onClick={() => setSettingsOpen(v => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.7)',
                  cursor: 'pointer',
                  padding: '8px 14px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '13px',
                  fontWeight: 500,
                  textAlign: 'left',
                  borderLeft: '3px solid transparent',
                }}
                aria-expanded={settingsOpen}
                aria-controls="sidebar-settings-group"
              >
                {/* No `gap` here: .nav-icon already carries margin-right:12px, so
                    the icon→label spacing matches Dashboard/Documents exactly
                    (a gap would double it to 24px and push "Settings" out of line). */}
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="nav-icon">{NAV_ICONS.settings}</span>
                  <span className="nav-label">Settings</span>
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     style={{ transition: 'transform 0.2s ease', transform: settingsOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              {settingsOpen && (
                <div id="sidebar-settings-group">
                  {visibleItems.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`nav-item ${isActive(item.to) ? 'active' : ''}`}
                      style={{ paddingLeft: '32px', fontSize: '12.5px' }}
                    >
                      <span className="nav-icon" style={{ marginRight: '8px' }}>{NAV_ICONS[item.icon]}</span>
                      <span className="nav-label">{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Phase 14 Step 3 — legacy "Admin Console" link removed; all admin features now live at their own routes and overview stats appear at the top of /dashboard for admins. */}

        {/* Phase 7 item 16i — Logout removed from sidebar (now lives in the Header avatar dropdown) */}
      </nav>

      {/* Recycle Bin: a bin at the very bottom of the sidebar, for whoever may delete. */}
      {(userRole === 'tenant_admin' || userRole === 'super_admin' || hasPerm('delete_document')) && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '8px 0' }}>
          <Link to="/recycle-bin" title="Recycle Bin"
            className={`nav-item ${isActive('/recycle-bin') ? 'active' : ''}`}>
            <span className="nav-icon">{NAV_ICONS.trash}</span>
            <span className="nav-label">Recycle Bin</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
